import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material'
import { GreenSwitch } from 'modules/inventory/components/IsActiveSwitch'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'

import PageWrapper from 'common/components/PageWrapper'
import useInfiniteScrollTable from 'common/components/BasicTable/useInfiniteScrollTable'
import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import { useDefaultValueLabel } from 'common/utils/defaultValueLabel'
import { fNumber } from 'common/utils/formatNumber'
import { useProductCategory, useProductV3ById, useStocksV3, useWarehouse } from 'modules/inventory/api/queries'
import { useMutateRemoveProduct, useMutateRemoveStock, useMutateUpdateStockStatusV3 } from 'modules/inventory/api/mutation'
import { Stock, StockV3 } from 'modules/inventory/api/type'
import StockDialog, { StockMode } from 'modules/inventory/components/StockDialog'
import { formatThaiShortBEWithTime } from 'modules/inventory/helper/format_date'
import CreateProductDialog from 'modules/inventory/pages/product/CreateProductPage'
import AddStockDialog from 'modules/inventory/pages/stock/CreateStock'
import { usePermitToActionFeature } from 'modules/user/api/selector'

const toStockCompat = (s: StockV3): Stock => ({
  id: s.stock_id,
  lot_number: s.lot_number ?? '',
  is_active: s.is_active,
  created_at: s.created_at,
  product: {
    id: s.product_id,
    image_url: s.product_img_url,
    name: s.product_name,
    category: { id: '', name: '' },
  },
  warehouse: { id: s.warehouse_id, name: s.warehouse_name },
  quantity: s.quantity,
  unit: s.unit_abbreviation,
  price: s.price_per_lot,
  price_per_unit: s.price_per_unit,
})

const CATEGORY_COLORS = [
  { bg: '#E3F2FD', text: '#1565C0' },
  { bg: '#FFF3E0', text: '#E65100' },
  { bg: '#F3E5F5', text: '#6A1B9A' },
  { bg: '#E8F5E9', text: '#2E7D32' },
  { bg: '#FBE9E7', text: '#BF360C' },
  { bg: '#E0F2F1', text: '#00695C' },
  { bg: '#FCE4EC', text: '#880E4F' },
  { bg: '#F9FBE7', text: '#558B2F' },
]

function getCategoryColor(idx: number) {
  return CATEGORY_COLORS[idx % CATEGORY_COLORS.length]
}

interface StatBoxProps {
  icon: React.ReactNode
  value: string | number
  label: string
  valueColor?: string
}

const StatBox = ({ icon, value, label, valueColor, }: StatBoxProps) => (
  <Card
    variant="outlined"
    sx={{
      flex: 1,
      borderRadius: 3,
      p: 2.5,
      bgcolor: 'common.white',
      minHeight: 'auto'
    }}
  >
    <Stack
      direction="row"
      spacing={2}
      alignItems={'flex-start'}
      sx={{ height: '100%' }}
    >
      <Box
        sx={{
          bgcolor: 'primary.main',
          borderRadius: 2,
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          textAlign: 'right'
        }}
      >
        <Typography
          variant="h3"
          fontWeight={700}
          sx={{ color: valueColor ?? 'text.primary', lineHeight: 1.2 }}
        >
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Stack>
  </Card>
)

const ProductDetail = () => {
  const { t } = useTranslation('product')
  const label = useDefaultValueLabel()
  const theme = useTheme()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const canUpdateProduct = usePermitToActionFeature([CRUDAction.Update], FeatureKey.Inventory)
  const canDeleteProduct = usePermitToActionFeature([CRUDAction.Delete], FeatureKey.Inventory)
  const canAddStock = usePermitToActionFeature([CRUDAction.Create], FeatureKey.Inventory)

  const { data: product } = useProductV3ById(id)
  const { data: categories = [] } = useProductCategory()
  const [stockStatus, setStockStatus] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE')
  const { data, isLoading, hasNextPage, fetchNextPage } = useStocksV3({ productId: id, stockStatus })
  const { data: warehouseData } = useWarehouse()
  const warehouses = warehouseData?.pages.flatMap((p) => p.warehouses) ?? []

  const catIdx = categories.findIndex((c) => c.id === (product?.category_id ?? ''))
  const chipColor = getCategoryColor(catIdx >= 0 ? catIdx : 0)
  const isNegativeStock = (product?.stock_quantity_active ?? 0) <= 0
  const activeStockCount = product?.stock_count_active ?? 0
  const inactiveStockCount = (product?.stock_count ?? 0) - activeStockCount
  const updateStatusMutation = useMutateUpdateStockStatusV3()
  const removeProductMutation = useMutateRemoveProduct(20)
  const removeStockMutation = useMutateRemoveStock()

  const { dialog: deleteDialog, openDialog: openConfirmDeleteDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      if (id) {
        await removeProductMutation.mutateAsync(id, {
          onSuccess: () => navigate('/dashboard/product'),
        })
      }
    },
  })

  const [deleteStockTarget, setDeleteStockTarget] = useState<StockV3 | null>(null)

  const { dialog: deleteStockDialog, openDialog: openConfirmDeleteStockDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      if (deleteStockTarget) {
        await removeStockMutation.mutateAsync(deleteStockTarget.stock_id)
        setDeleteStockTarget(null)
      }
    },
  })

  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set())
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedStock, setSelectedStock] = useState<StockV3 | null>(null)
  const [dialogMode, setDialogMode] = useState<StockMode | null>(null)
  const [dialogStock, setDialogStock] = useState<StockV3 | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [addStockDialogOpen, setAddStockDialogOpen] = useState(false)
  const [editStockId, setEditStockId] = useState<string | undefined>(undefined)

  const handleToggleStatus = (stockId: string, currentIsActive: boolean) => {
    setTogglingIds((prev) => new Set(prev).add(stockId))
    updateStatusMutation.mutate(
      { stockId, is_active: !currentIsActive },
      {
        onSettled: () =>
          setTogglingIds((prev) => {
            const next = new Set(prev)
            next.delete(stockId)
            return next
          }),
      }
    )
  }

  const stocks = data?.pages.flatMap((p) => p.stocks) ?? []

  const handleScroll = useInfiniteScrollTable({
    isLoading,
    onLoading: () => {
      if (hasNextPage) fetchNextPage()
    },
  })

  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>, row: StockV3) => {
    setMenuAnchorEl(e.currentTarget)
    setSelectedStock(row)
  }

  const handleCloseMenu = () => {
    setMenuAnchorEl(null)
    setSelectedStock(null)
  }

  const handleOpenDialog = (mode: StockMode) => {
    setDialogStock(selectedStock)
    setDialogMode(mode)
    handleCloseMenu()
  }

  const handleCloseDialog = () => {
    setDialogMode(null)
    setDialogStock(null)
  }

  const LINKS = [
    { name: t('inventory.title'), href: '/dashboard/product' },
    { name: t('product.detail') },
  ]

  return (
    <PageWrapper
      title={
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton size="small" onClick={() => navigate('/dashboard/product')}>
            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography variant="h4">{t('product.detail')}</Typography>
        </Stack>
      }
      titleHead={t('product.detail')}
      links={LINKS}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{
        mb: 3, justifyContent: { xs: 'flex-start', sm: 'space-between' },
      }}>
        <Stack direction="row" sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<HistoryOutlinedIcon />}
            sx={{ backgroundColor: 'white' }}
            onClick={() => navigate('/dashboard/stock', { state: { productId: id } })}
          >
            {t('inventory.stock_history')}
          </Button>
        </Stack>

        <Stack direction="row" spacing={1.5} >
          {canDeleteProduct && (
            <Button variant="outlined"
              color="inherit"
              startIcon={<DeleteOutlineIcon />}
              sx={{ backgroundColor: 'white' }}
              onClick={openConfirmDeleteDialog}
            >
              {t('product.delete')}
            </Button>
          )}
          {canUpdateProduct && (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<EditOutlinedIcon />}
              sx={{ backgroundColor: 'white' }}
              onClick={() => setEditDialogOpen(true)}
            >
              {t('product.edit')}
            </Button>
          )}
          {canAddStock && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddStockDialogOpen(true)}
            >
              {t('inventory.add_lot')}
            </Button>
          )}
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Card
          variant="outlined"
          sx={{ flex: 1, borderRadius: 3, p: 2.5, bgcolor: 'common.white' }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={product?.product_img_url ?? undefined}
              alt={product?.product_name}
              sx={{ width: 56, height: 56, borderRadius: 2 }}
            />
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {product?.product_name ?? '-'}
              </Typography>
              {product?.category_name && (
                <Chip
                  label={label.inventoryCategory(product.category_name)}
                  size="small"
                  sx={{
                    mt: 0.5,
                    bgcolor: chipColor.bg,
                    color: chipColor.text,
                    fontWeight: 600,
                    fontSize: 12,
                    height: 24,
                    borderRadius: 1,
                  }}
                />
              )}
            </Box>
          </Stack>
        </Card>

        <StatBox
          icon={<Box
            component="img"
            src= {isNegativeStock ? "/static/icons/inventory/out_stock.svg" : "/static/icons/inventory/all_stock.svg"}
            alt="Total"
            sx={{ width: 24, height: 24 }}
          />}
          value={`${fNumber(product?.stock_quantity_active ?? 0)}`}
          label={t('product.remaining')}
          valueColor={isNegativeStock ? 'error.main' : 'blaack'}
        />
        <StatBox
          icon={<Box
            component="img"
            src="/static/icons/inventory/use_lot.svg"
            alt="Use"
            sx={{ width: 24, height: 24 }}
          />}
          value={product?.stock_count_active ?? 0}
          label={t('inventory.active_lots')}
        />
        <StatBox
          icon={<Box
            component="img"
            src="/static/icons/inventory/baht_price.svg"
            alt="Price"
            sx={{ width: 24, height: 24 }}
          />}
          value={
            product?.stock_latest_price_per_unit_active != null
              ? `฿${fNumber(product.stock_latest_price_per_unit_active)}`
              : '-'
          }
          label={t('inventory.latest_price')}
        />
      </Stack>

      <Card sx={{ borderRadius: '24px', p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          {t('product.detail')}
        </Typography>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={stockStatus}
            onChange={(_e, newValue) => setStockStatus(newValue)}
            textColor="inherit"
            TabIndicatorProps={{
              style: { backgroundColor: theme.palette.primary.main },
            }}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 400,
                fontSize: '16px',
                color: theme.palette.text.primary,
              },
              '& .MuiTab-root.Mui-selected': {
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            }}
          >
            <Tab value="ACTIVE" label={`${t('inventory.tab_active')}(${activeStockCount})`} />
            <Tab value="INACTIVE" label={`${t('inventory.tab_inactive')}(${inactiveStockCount})`} />
          </Tabs>
        </Box>
        <TableContainer
          component={Paper}
          onScroll={handleScroll}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          <Table
            stickyHeader
            sx={{
              '& thead th': {
                fontSize: '14px',
                color: theme.palette.common.black,
                backgroundColor: '#F0F0F0',
                borderBottom: '1px solid',
                borderColor: 'divider',
              },
              '& tbody td': { borderBottom: '1px solid', borderColor: 'divider' },
              '& thead th:first-of-type': { borderTopLeftRadius: 12 },
              '& thead th:last-of-type': { borderTopRightRadius: 12 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('inventory.lot')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('inventory.received_date')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('product.history.warehouse')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('product.remaining')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('inventory.price_per_unit')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('inventory.price_per_lot')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('product.status')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {stocks.map((row, idx) => (
                <TableRow key={row.stock_id || idx} hover>
                  <TableCell>{row.lot_number ?? '-'}</TableCell>
                  <TableCell>{formatThaiShortBEWithTime(row.created_at)}</TableCell>
                  <TableCell>{row.warehouse_name || '-'}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ color: row.quantity <= 0 ? 'error.main' : 'text.primary' }}
                    >
                      {fNumber(row.quantity)} {row.unit_abbreviation}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    ฿{fNumber(row.price_per_unit)}/{row.unit_abbreviation}
                  </TableCell>
                  <TableCell>฿{fNumber(row.price_per_lot)}</TableCell>
                  <TableCell>
                    {canUpdateProduct ? (
                      <GreenSwitch
                        checked={row.is_active}
                        disabled={togglingIds.has(row.stock_id)}
                        onChange={() => handleToggleStatus(row.stock_id, row.is_active)}
                      />
                    ) : (
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: row.is_active ? 'success.main' : 'text.disabled',
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {row.is_active ? t('inventory.tab_active') : t('inventory.tab_inactive')}
                        </Typography>
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {(canUpdateProduct || canDeleteProduct) && (
                      <IconButton size="small" onClick={(e) => handleOpenMenu(e, row)}>
                        <MoreVertIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{ elevation: 2, sx: { borderRadius: 2, minWidth: 160 } }}
      >
        {canUpdateProduct && (
          <MenuItem
            onClick={() => {
              setEditStockId(selectedStock?.stock_id)
              setAddStockDialogOpen(true)
              handleCloseMenu()
            }}
          >
            {t('product.edit')}
          </MenuItem>
        )}
        {canUpdateProduct && (
          <MenuItem onClick={() => handleOpenDialog('edit-qty')}>{t('inventory.adjust_qty')}</MenuItem>
        )}
        {canUpdateProduct && (
          <MenuItem onClick={() => handleOpenDialog('warehouse-transfer')}>{t('inventory.transfer_warehouse')}</MenuItem>
        )}
        {canDeleteProduct && (
          <MenuItem sx={{ color: 'error.main' }} onClick={() => {
            setDeleteStockTarget(selectedStock)
            handleCloseMenu()
            openConfirmDeleteStockDialog()
          }}>
            {t('product.delete')}
          </MenuItem>
        )}
      </Menu>
      {dialogMode && dialogStock && (
        <StockDialog
          open={Boolean(dialogMode)}
          onClose={handleCloseDialog}
          onDone={handleCloseDialog}
          mode={dialogMode}
          stock={toStockCompat(dialogStock)}
          warehouses={warehouses}
          pageSize={20}
        />
      )}
      {deleteDialog}
      {deleteStockDialog}
      <CreateProductDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        productId={id}
      />
      {product && (
        <AddStockDialog
          open={addStockDialogOpen}
          onClose={() => { setAddStockDialogOpen(false); setEditStockId(undefined) }}
          product={product}
          stockId={editStockId}
        />
      )}
    </PageWrapper>
  )
}

export default ProductDetail
